import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserPlus,
  Upload,
  Pencil,
  Eye,
  FileText,
  FileImage,
  Download,
  ListChecks,
  XCircle,
  Ban,
  ArrowRight,
  CalendarCheck,
  BadgeCheck,
  X,
  Trash2,
  Loader2,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { useLookupValues } from "@/lib/platformConfig";
import { documentOwnerLabelForRole } from "./admissionDocumentOwnership";
import { documentPreviewIssue } from "./admissionDocumentPreview";

const DEFAULT_STAGE_TONE = "bg-slate-100 text-slate-700";

const toLocalDate = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toDateValue = (date) =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : "";

const calculateAge = (dateOfBirth) => {
  const birthDate = toLocalDate(dateOfBirth);
  if (!birthDate) return "";

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const birthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());
  if (!birthdayPassed) age -= 1;
  return String(age);
};

function DatePickerField({ value, onChange, testId, invalid }) {
  return (
    <DatePicker
      value={value}
      onChange={(dateValue) => onChange(toLocalDate(dateValue))}
      fromYear={new Date().getFullYear() - 18}
      toYear={new Date().getFullYear()}
      max={new Date().toISOString().slice(0, 10)}
      ariaLabel="Date of birth"
      testId={testId}
      className={invalid ? "border-red-500" : ""}
    />
  );
}

const TRANSITION_BUTTON_META = {
  documents_pending: {
    className:
      "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100",
  },
  shortlisted: {
    className:
      "border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-400 hover:bg-sky-100",
  },
  interview_scheduled: {
    className:
      "border-orange-200 bg-orange-50 text-orange-700 hover:border-orange-400 hover:bg-orange-100",
  },
  offered: {
    className:
      "border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-400 hover:bg-violet-100",
  },
  rejected: {
    className:
      "border-red-200 bg-red-50 text-red-700 hover:border-red-400 hover:bg-red-100",
  },
  withdrawn: {
    className:
      "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-400 hover:bg-slate-100",
  },
};

const TRANSITION_ICONS = {
  documents_pending: FileText,
  shortlisted: ListChecks,
  interview_scheduled: CalendarCheck,
  offered: BadgeCheck,
  rejected: XCircle,
  withdrawn: Ban,
};

const humanizeStatus = (value) =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");

const REQUIRED_FIELDS = [
  "full_name",
  "address_street",
  "address_apartment",
  "age",
  "date_of_birth",
  "place_of_birth",
  "uae_citizen",
  "nationality",
  "father_guardian_name",
  "father_guardian_phone",
  "father_guardian_email",
  "father_guardian_education_level",
  "father_guardian_occupation",
  "father_guardian_work_details",
  "mother_name",
  "mother_phone",
  "mother_email",
  "mother_education_level",
  "mother_occupation",
  "mother_work_details",
  "emergency_contact_no",
  "grade_applied_id",
  "academic_year_id",
];

const REQUIRED_DOCUMENTS = [
  ["emirates_id", "Emirates ID copy"],
  ["passport", "Passport copy"],
  ["visa", "Visa copy"],
];
const OPTIONAL_DOCUMENTS = [["vaccination", "Vaccination copy"]];
const DOCUMENTS = [...REQUIRED_DOCUMENTS, ...OPTIONAL_DOCUMENTS];
const MAX_DOCUMENT_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const EDIT_DOCUMENT_FILE_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);
const documentRowKey = (document) =>
  `${document.person_role || "application"}:${document.kind}`;
const CHILD_DOCUMENT_EXCLUSIONS = new Set(["insurance", "medical"]);
const DEFAULT_REQUIREMENT_BY_KIND = {
  emirates_id: "optional",
  passport: "always",
  visa: "optional",
};

const isImageDocument = (name = "", contentType = "") =>
  contentType.startsWith("image/") || /\.(jpe?g|png)$/i.test(name);

const documentRequirementFor = (
  application,
  role,
  documentKind,
  nationalityByValue,
) => {
  const requirement = documentKind?.metadata?.requirement;
  if (requirement === "always") return "required";
  if (requirement === "optional") return "optional";
  if (requirement !== "non_uae_citizen") return "unknown";

  const nationality = application.people?.[role]?.nationality;
  const nationalityMetadata = nationalityByValue[nationality]?.metadata;
  if (!nationalityMetadata) return "unknown";
  return nationalityMetadata.is_uae_citizen ? "not_applicable" : "required";
};

const createEditableDocuments = (
  application,
  documentKindsByRole = {},
  nationalityByValue = {},
) => {
  const allKinds = [...(documentKindsByRole.child || []), ...(documentKindsByRole.father || []), ...(documentKindsByRole.mother || [])];
  const isWizardApplication =
    application.schema_version === 2 || Array.isArray(application.documents);
  if (isWizardApplication) {
    const documentKindByValue = Object.fromEntries(
      allKinds.map((document) => [document.value, document]),
    );
    const rows = (application.documents || [])
      .filter(
        (document) =>
          document &&
          typeof document === "object" &&
          typeof document.person_role === "string" &&
          typeof document.kind === "string",
      )
      .map((document) => ({
        key: documentRowKey(document),
        source: "wizard",
        person_role: document.person_role,
        kind: document.kind,
        requirement: documentRequirementFor(
          application,
          document.person_role,
          (documentKindsByRole[document.person_role] || []).find((kind) => kind.value === document.kind) || documentKindByValue[document.kind],
          nationalityByValue,
        ),
        number: document.number || "",
        expiry_date: document.expiry_date || "",
        file_ids: Array.isArray(document.file_ids)
          ? document.file_ids.filter(
              (fileId) => typeof fileId === "string" && fileId,
            )
          : [],
        file_names: Array.isArray(document.file_names)
          ? document.file_names
          : [],
      }));
    const knownRows = new Set(rows.map((document) => document.key));
    for (const role of ["child", "father", "mother"]) {
      if (!application.people?.[role]) continue;
      for (const kind of documentKindsByRole[role] || []) {
        const candidate = { person_role: role, kind: kind.value };
        const requirement = documentRequirementFor(
          application,
          role,
          kind,
          nationalityByValue,
        );
        if (
          requirement === "not_applicable" ||
          knownRows.has(documentRowKey(candidate))
        ) {
          continue;
        }
        rows.push({
          key: documentRowKey(candidate),
          source: "wizard",
          person_role: role,
          kind: kind.value,
          requirement,
          number: "",
          expiry_date: "",
          file_ids: [],
          file_names: [],
        });
      }
    }
    return rows;
  }

  const documentFileIds = application.document_file_ids || {};
  const documentLabels = new Map(DOCUMENTS);
  Object.keys(documentFileIds).forEach((kind) => {
    if (!documentLabels.has(kind)) {
      documentLabels.set(kind, humanizeStatus(kind));
    }
  });
  return Array.from(documentLabels, ([kind, label]) => ({
    key: documentRowKey({ kind }),
    source: "legacy",
    person_role: "application",
    kind,
    requirement: "unknown",
    legacy_label: label,
    number: "",
    expiry_date: "",
    file_ids: documentFileIds[kind] ? [documentFileIds[kind]] : [],
    file_names: [],
  }));
};

const EDIT_FIELDS = [
  ["applicant_first_name", "First name"],
  ["applicant_last_name", "Last name"],
  ["age", "Age"],
  ["date_of_birth", "Date of birth"],
  ["place_of_birth", "Place of birth"],
  ["address_street", "Street address"],
  ["address_apartment", "Apartment / unit"],
  ["father_guardian_name", "Father / legal guardian"],
  ["father_guardian_phone", "Father phone"],
  ["father_guardian_email", "Father email"],
  ["father_guardian_education_level", "Father education"],
  ["father_guardian_occupation", "Father occupation"],
  ["father_guardian_work_details", "Father work details"],
  ["mother_name", "Mother name"],
  ["mother_phone", "Mother phone"],
  ["mother_email", "Mother email"],
  ["mother_education_level", "Mother education"],
  ["mother_occupation", "Mother occupation"],
  ["mother_work_details", "Mother work details"],
  ["emergency_contact_no", "Emergency contact no."],
];

export default function Admissions() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsStatus, setDetailsStatus] = useState(null);
  const [selectedDetailsApplication, setSelectedDetailsApplication] =
    useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [initialEditForm, setInitialEditForm] = useState({});
  const [editDocuments, setEditDocuments] = useState([]);
  const [editDocumentFiles, setEditDocumentFiles] = useState({});
  const [editDocumentsChanged, setEditDocumentsChanged] = useState(false);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sectionPickerApplication, setSectionPickerApplication] = useState(null);
  const [sectionPickerId, setSectionPickerId] = useState("");
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["applications"],
    queryFn: async () => unwrap(await api.get("/applications")),
  });
  const { data: drafts = [], isLoading: draftsLoading } = useQuery({
    queryKey: ["application-drafts"],
    queryFn: async () => unwrap(await api.get("/applications", { params: { submission_state: "draft" } })),
  });
  const { data: sections } = useQuery({
    queryKey: ["sections"],
    queryFn: async () => unwrap(await api.get("/sections")),
  });
  const admissionLookup = useLookupValues("admission_statuses");
  const childDocumentLookup = useLookupValues("student_document_kinds");
  const parentDocumentLookup = useLookupValues("parent_document_kinds");
  const legacyDocumentLookup = useLookupValues("admission_document_kinds");
  const nationalityLookup = useLookupValues("nationalities");
  const qc = useQueryClient();

  const normalizeDocKind = React.useCallback((row, personRoles) => {
    const metadata = row?.metadata && typeof row.metadata === "object" ? { ...row.metadata } : {};
    const requirement = ["always", "optional", "non_uae_citizen"].includes(metadata.requirement)
      ? metadata.requirement
      : (DEFAULT_REQUIREMENT_BY_KIND[row.value] || "optional");
    return {
      ...row,
      metadata: {
        ...metadata,
        person_roles: personRoles,
        requirement,
        upload_only: Boolean(metadata.upload_only) || row.value === "vaccination",
      },
    };
  }, []);

  const childAdmissionDocumentKinds = React.useMemo(
    () => {
      const rows = (childDocumentLookup.data?.values || [])
        .filter((document) => typeof document?.value === "string" && document.value && !CHILD_DOCUMENT_EXCLUSIONS.has(document.value))
        .map((document) => normalizeDocKind(document, ["child"]));
      if (rows.length) return rows;
      return (legacyDocumentLookup.data?.values || [])
        .filter((document) => typeof document?.value === "string" && document.value && !CHILD_DOCUMENT_EXCLUSIONS.has(document.value) && (document?.metadata?.person_roles || []).includes("child"))
        .map((document) => normalizeDocKind(document, ["child"]));
    },
    [childDocumentLookup.data?.values, legacyDocumentLookup.data?.values, normalizeDocKind],
  );

  const parentAdmissionDocumentKinds = React.useMemo(
    () => {
      const rows = (parentDocumentLookup.data?.values || [])
        .filter((document) => typeof document?.value === "string" && document.value)
        .map((document) => {
          const roles = (document?.metadata?.person_roles || ["father", "mother"]).filter((role) => role === "father" || role === "mother");
          return normalizeDocKind(document, roles.length ? roles : ["father", "mother"]);
        });
      if (rows.length) return rows;
      return (legacyDocumentLookup.data?.values || [])
        .filter((document) => typeof document?.value === "string" && document.value !== "vaccination" && (document?.metadata?.person_roles || []).some((role) => role === "father" || role === "mother"))
        .map((document) => {
          const roles = (document?.metadata?.person_roles || []).filter((role) => role === "father" || role === "mother");
          return normalizeDocKind(document, roles.length ? roles : ["father", "mother"]);
        });
    },
    [parentDocumentLookup.data?.values, legacyDocumentLookup.data?.values, normalizeDocKind],
  );

  const admissionDocumentKindsByRole = React.useMemo(
    () => ({
      child: childAdmissionDocumentKinds,
      father: parentAdmissionDocumentKinds,
      mother: parentAdmissionDocumentKinds,
    }),
    [childAdmissionDocumentKinds, parentAdmissionDocumentKinds],
  );

  const admissionDocumentKinds = React.useMemo(
    () => [
      ...childAdmissionDocumentKinds,
      ...parentAdmissionDocumentKinds,
    ],
    [childAdmissionDocumentKinds, parentAdmissionDocumentKinds],
  );
  const admissionDocumentKindByValue = React.useMemo(
    () =>
      Object.fromEntries(
        admissionDocumentKinds.map((document) => [document.value, document]),
      ),
    [admissionDocumentKinds],
  );
  const nationalityByValue = React.useMemo(
    () =>
      Object.fromEntries(
        (nationalityLookup.data?.values || []).map((nationality) => [
          nationality.value,
          nationality,
        ]),
      ),
    [nationalityLookup.data?.values],
  );

  React.useEffect(
    () => () => {
      if (documentPreview?.url) URL.revokeObjectURL(documentPreview.url);
    },
    [documentPreview?.url],
  );

  const admissionStatuses = React.useMemo(() => {
    const rows = (admissionLookup.data?.values || []).filter(
      (row) => typeof row?.value === "string" && row.value,
    );
    if (!rows.length) {
      const observedStatuses = Array.from(
        new Set(
          (data || [])
            .map((application) => application?.status)
            .filter((status) => typeof status === "string" && status),
        ),
      );
      return observedStatuses.map((status, index) => ({
        key: status,
        label: humanizeStatus(status),
        tone: DEFAULT_STAGE_TONE,
        sort_order: (index + 1) * 10,
        metadata: {},
      }));
    }

    return rows
      .map((row, index) => {
        return {
          key: row.value,
          label: row.label || humanizeStatus(row.value),
          tone: row?.metadata?.tone || DEFAULT_STAGE_TONE,
          sort_order: Number.isFinite(row.sort_order)
            ? row.sort_order
            : (index + 1) * 10,
          metadata:
            row.metadata && typeof row.metadata === "object"
              ? row.metadata
              : {},
        };
      })
      .sort(
        (a, b) =>
          a.sort_order - b.sort_order || a.label.localeCompare(b.label),
      );
  }, [admissionLookup.data?.values, data]);

  const admissionStatusByKey = React.useMemo(
    () => Object.fromEntries(admissionStatuses.map((status) => [status.key, status])),
    [admissionStatuses],
  );

  const transitionMap = React.useMemo(() => {
    const allowedStatuses = new Set(admissionStatuses.map((status) => status.key));
    const map = {};
    for (const status of admissionStatuses) {
      const next = Array.isArray(status.metadata?.next_statuses)
        ? status.metadata.next_statuses
        : [];
      map[status.key] = next.filter((candidate) => allowedStatuses.has(candidate) && candidate !== "enrolled");
    }
    return map;
  }, [admissionStatuses]);

  const convertibleStatuses = React.useMemo(() => {
    return new Set(
      admissionStatuses
      .filter((status) => status.metadata?.can_convert === true)
      .map((status) => status.key),
    );
  }, [admissionStatuses]);

  const pipelineStatuses = React.useMemo(
    () => admissionStatuses.filter((status) => status.key !== "enrolled" && status.key !== "rejected"),
    [admissionStatuses],
  );

  const statusLabel = (status) =>
    admissionStatusByKey[status]?.label || humanizeStatus(status);

  const transitionMut = useMutation({
    mutationFn: async ({ id, to }) =>
      unwrap(
        await api.post(`/applications/${id}/transition`, {
          to_status: to,
          note: `Moved via UI`,
        }),
      ),
    onSuccess: () => {
      toast.success("Status updated");
      refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const convertMut = useMutation({
    mutationFn: async ({ id, section_id }) =>
      unwrap(await api.post(`/applications/${id}/convert`, section_id ? { section_id } : {})),
    onSuccess: (d) => {
      toast.success(`Enrolled as ${d.admission_no}`);
      refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async ({ id, kind }) =>
      unwrap(
        await api.delete(
          kind === "draft"
            ? `/applications/${id}/draft`
            : `/applications/${id}`,
        ),
      ),
    onSuccess: (_data, target) => {
      toast.success(target.kind === "draft" ? "Draft deleted" : "Application deleted");
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["application-drafts"] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const closeEdit = () => {
    setEditOpen(false);
    setEditingApplication(null);
    setInitialEditForm({});
    setEditDocuments([]);
    setEditDocumentFiles({});
    setEditDocumentsChanged(false);
    setDocumentPreview(null);
  };

  const updateEditDocument = (documentKey, changes) => {
    setEditDocuments((current) =>
      current.map((document) =>
        document.key === documentKey ? { ...document, ...changes } : document,
      ),
    );
    setEditDocumentsChanged(true);
  };

  const selectEditDocument = (document, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (
      files.some(
        (file) =>
          !EDIT_DOCUMENT_FILE_TYPES.has(file.type) &&
          !/\.(pdf|jpe?g|png)$/i.test(file.name),
      )
    ) {
      toast.error("Upload a PDF, JPEG, or PNG file.");
      return;
    }
    if (files.some((file) => file.size > MAX_DOCUMENT_FILE_SIZE_BYTES)) {
      toast.error("Each document must be 10 MB or smaller.");
      return;
    }
    const pendingFiles = editDocumentFiles[document.key] || [];
    const maximumFiles = document.source === "legacy" ? 1 : 5;
    if (document.file_ids.length + pendingFiles.length + files.length > maximumFiles) {
      toast.error(
        maximumFiles === 1
          ? "Remove the current file before choosing a replacement."
          : "A document can have up to five files.",
      );
      return;
    }
    setEditDocumentFiles((current) => ({
      ...current,
      [document.key]: [...(current[document.key] || []), ...files],
    }));
    setEditDocumentsChanged(true);
  };

  const removeStoredEditDocumentFile = (documentKey, fileIndex) => {
    setEditDocuments((current) =>
      current.map((document) => {
        if (document.key !== documentKey) return document;
        return {
          ...document,
          file_ids: document.file_ids.filter(
            (_, index) => index !== fileIndex,
          ),
          file_names: document.file_names.filter(
            (_, index) => index !== fileIndex,
          ),
        };
      }),
    );
    setEditDocumentsChanged(true);
  };

  const removePendingEditDocumentFile = (documentKey, fileIndex) => {
    setEditDocumentFiles((current) => {
      const next = { ...current };
      const files = (next[documentKey] || []).filter(
        (_, index) => index !== fileIndex,
      );
      if (files.length) next[documentKey] = files;
      else delete next[documentKey];
      return next;
    });
    setEditDocumentsChanged(true);
  };

  const uploadEditedDocuments = async () => {
    const updatedDocuments = [];
    for (const document of editDocuments) {
      const fileIds = [...document.file_ids];
      const fileNames = [...document.file_names];
      for (const file of editDocumentFiles[document.key] || []) {
        const body = new FormData();
        body.append("file", file);
        body.append("is_sensitive", "true");
        const uploaded = unwrap(
          await api.post("/files/upload", body, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
        );
        fileIds.push(uploaded.id);
        fileNames.push(file.name);
      }
      updatedDocuments.push({ ...document, file_ids: fileIds, file_names: fileNames });
    }
    return updatedDocuments;
  };

  const openStoredDocumentPreview = async (fileId, fileName) => {
    if (!editingApplication?.id) return;
    setDocumentPreview({ fileName, loading: true, url: "", contentType: "" });
    try {
      const response = await api.get(
        `/applications/${editingApplication.id}/documents/${fileId}/preview`,
        { responseType: "blob" },
      );
      const contentType = response.headers["content-type"] || response.data.type;
      setDocumentPreview({
        fileName,
        loading: false,
        url: URL.createObjectURL(response.data),
        contentType,
        issue: await documentPreviewIssue(response.data, fileName, contentType),
      });
    } catch (error) {
      setDocumentPreview(null);
      toast.error(errMsg(error));
    }
  };

  const openPendingDocumentPreview = async (file) => {
    setDocumentPreview({
      fileName: file.name,
      loading: false,
      url: URL.createObjectURL(file),
      contentType: file.type,
      issue: await documentPreviewIssue(file, file.name, file.type),
    });
  };

  const editMut = useMutation({
    mutationFn: async () => {
      const changes = Object.fromEntries(
        EDIT_FIELDS.filter(
          ([key]) => String(editForm[key] ?? "") !== String(initialEditForm[key] ?? ""),
        ).map(([key]) => [key, key === "age" ? Number(editForm.age) : editForm[key] ?? ""]),
      );
      if (editForm.uae_citizen !== initialEditForm.uae_citizen) {
        changes.uae_citizen = editForm.uae_citizen === "yes";
      }
      if (editDocumentsChanged) {
        const updatedDocuments = await uploadEditedDocuments();
        if (editingApplication?.schema_version === 2) {
          changes.documents = updatedDocuments.map(
            ({ person_role, kind, number, expiry_date, file_ids }) => ({
              person_role,
              kind,
              number,
              expiry_date,
              file_ids,
            }),
          );
        } else {
          changes.document_file_ids = Object.fromEntries(
            updatedDocuments
              .filter((document) => document.file_ids[0])
              .map((document) => [document.kind, document.file_ids[0]]),
          );
        }
      }
      return unwrap(
        await api.patch(`/applications/${editingApplication.id}`, changes),
      );
    },
    onSuccess: () => {
      toast.success("Application updated");
      qc.invalidateQueries({ queryKey: ["applications"] });
      closeEdit();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const groupedByStatus = React.useMemo(() => {
    const map = {};
    for (const status of admissionStatuses) map[status.key] = [];
    for (const a of data || []) {
      if (!map[a.status]) map[a.status] = [];
      map[a.status].push(a);
    }
    return map;
  }, [data, admissionStatuses]);

  const openEdit = (application) => {
    setEditingApplication(application);
    setEditDocuments(
      createEditableDocuments(
        application,
        admissionDocumentKindsByRole,
        nationalityByValue,
      ),
    );
    setEditDocumentFiles({});
    setEditDocumentsChanged(false);
    const nextEditForm = {
      ...application,
      ...Object.fromEntries(
        EDIT_FIELDS.map(([key]) => [
          key,
          key === "age"
            ? String(application[key] ?? "")
            : application[key] ?? "",
        ]),
      ),
      document_file_ids: { ...(application.document_file_ids || {}) },
      applicant_first_name: application.applicant_first_name || "",
      applicant_last_name: application.applicant_last_name || "",
      age: calculateAge(application.date_of_birth),
      uae_citizen: application.uae_citizen ? "yes" : "no",
      address_street:
        application.address_street || application.address_json?.street || "",
      address_apartment:
        application.address_apartment ||
        application.address_json?.apartment ||
        "",
      father_guardian_work_details:
        application.father_guardian_work_details ||
        application.father_guardian_workplace ||
        "",
      mother_occupation: application.mother_occupation || "",
    };
    setEditForm(nextEditForm);
    setInitialEditForm(nextEditForm);
    setEditOpen(true);
  };

  const detailsStatusLabel = detailsStatus ? statusLabel(detailsStatus) : "";
  const editDocumentGroups = editDocuments.reduce((groups, document) => {
    const role = document.person_role || "application";
    const group = groups.find((item) => item.role === role);
    if (group) group.documents.push(document);
    else groups.push({ role, documents: [document] });
    return groups;
  }, []);
  const attachedEditDocumentCount = editDocuments.reduce(
    (count, document) =>
      count +
      document.file_ids.length +
      (editDocumentFiles[document.key] || []).length,
    0,
  );
  const documentTypesWithFiles = editDocuments.filter(
    (document) =>
      document.file_ids.length || (editDocumentFiles[document.key] || []).length,
  ).length;
  const hasEditFormChanges = EDIT_FIELDS.some(
    ([key]) => String(editForm[key] ?? "") !== String(initialEditForm[key] ?? ""),
  ) || editForm.uae_citizen !== initialEditForm.uae_citizen;
  const hasEditChanges = hasEditFormChanges || editDocumentsChanged;
  const canDeleteApplication = (application) =>
    !application.converted_student_id && application.status !== "enrolled";
  const requestDelete = (application, kind) => {
    setDeleteTarget({
      id: application.id,
      kind,
      name: `${application.applicant_first_name || application.people?.child?.first_name || "Untitled"} ${application.applicant_last_name || application.people?.child?.last_name || ""}`.trim(),
    });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="admissions-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
            {t("management.admissionsTitle")}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Application pipeline — {(data || []).length} total
          </p>
        </div>
        <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-end"></div>
        <Button
          className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
          data-testid="new-application-btn"
          onClick={() => navigate("/admissions/new")}
        >
          <UserPlus className="mr-1.5 h-4 w-4" /> New application
        </Button>
        <AlertDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen && !deleteMut.isPending) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">
                {deleteTarget?.kind === "draft" ? "Delete draft?" : "Delete application?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.name || "This record"} will be removed from the admissions pipeline. Enrolled student records cannot be deleted here.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={deleteMut.isPending}
                data-testid="cancel-application-delete"
              >
                Keep it
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
                disabled={deleteMut.isPending}
                data-testid="confirm-application-delete"
              >
                <Trash2 className="h-4 w-4" />
                {deleteMut.isPending
                  ? "Deleting..."
                  : deleteTarget?.kind === "draft"
                    ? "Delete draft"
                    : "Delete application"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display capitalize">
                {detailsStatusLabel} applications
              </DialogTitle>
              <DialogDescription className="sr-only">
                Review applications in the selected status and open each record for details.
              </DialogDescription>
            </DialogHeader>
            {selectedDetailsApplication ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 rounded-md border border-[var(--subtle)] bg-[var(--bg)] p-4">
                  {[
                    [
                      "Application no.",
                      selectedDetailsApplication.application_no,
                    ],
                    [
                      "Status",
                      statusLabel(selectedDetailsApplication.status),
                    ],
                    [
                      "Child",
                      `${selectedDetailsApplication.applicant_first_name || ""} ${selectedDetailsApplication.applicant_last_name || ""}`,
                    ],
                    ["Date of birth", selectedDetailsApplication.date_of_birth],
                    ["Age", selectedDetailsApplication.age],
                    ["Gender", selectedDetailsApplication.gender],
                    [
                      "Guardian phone",
                      selectedDetailsApplication.guardian_phone ||
                        selectedDetailsApplication.father_guardian_phone,
                    ],
                    [
                      "Guardian email",
                      selectedDetailsApplication.father_guardian_email ||
                        selectedDetailsApplication.mother_email,
                    ],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                        {label}
                      </div>
                      <div className="mt-1 text-sm font-medium text-[var(--text-primary)]">
                        {value || "Not provided"}
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedDetailsApplication(null)}
                >
                  Back to {detailsStatusLabel} applications
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {(groupedByStatus[detailsStatus] || []).length ? (
                  (groupedByStatus[detailsStatus] || []).map((app) => (
                    <div
                      key={app.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-[var(--subtle)] bg-[var(--bg)] p-3"
                    >
                      <div>
                        <div className="font-semibold">
                          {app.applicant_first_name} {app.applicant_last_name}
                        </div>
                        <div className="text-xs text-[var(--text-muted)]">
                          {app.application_no} ·{" "}
                          {app.guardian_phone ||
                            app.father_guardian_phone ||
                            "No phone"}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setSelectedDetailsApplication(app)}
                        aria-label={`View details for ${app.applicant_first_name} ${app.applicant_last_name}`}
                        title="View details"
                        data-testid={`details-view-${app.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="py-8 text-center text-sm text-[var(--text-muted)]">
                    No {detailsStatusLabel} applications yet.
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog
          open={editOpen}
          onOpenChange={(nextOpen) => {
            if (nextOpen) setEditOpen(true);
            else closeEdit();
          }}
        >
          <DialogContent className="flex h-[calc(100vh-1rem)] w-[calc(100vw-1rem)] max-w-7xl flex-col gap-0 overflow-hidden p-0 sm:h-[calc(100vh-2rem)]">
            <DialogHeader className="border-b border-[var(--subtle)] bg-white px-5 py-4 pr-12 sm:px-6">
              <DialogTitle className="font-display text-xl text-[var(--text-primary)]">
                Edit application
              </DialogTitle>
              <DialogDescription className="sr-only">
                Update the application details and attached documents.
              </DialogDescription>
              <p className="text-xs text-[var(--text-muted)]">
                {editingApplication?.application_no || "Application"} · {editingApplication?.applicant_first_name} {editingApplication?.applicant_last_name}
              </p>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <section className="px-5 py-5 sm:px-6 sm:py-6">
                <div className="mb-5 flex items-center justify-between border-b border-[var(--subtle)] pb-3">
                  <div>
                    <h3 className="font-display text-base font-bold text-[var(--text-primary)]">
                      Application details
                    </h3>
                    <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                      Child and family information
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
                  {EDIT_FIELDS.map(([key, label]) => (
                    <div
                      key={key}
                      className={
                        key === "father_guardian_work_details" ||
                        key === "mother_work_details" ||
                        key === "emergency_contact_no"
                          ? "sm:col-span-2 xl:col-span-3"
                          : ""
                      }
                    >
                      <Label className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                        {label}
                      </Label>
                      {key === "date_of_birth" ? (
                        <DatePickerField
                          value={editForm[key] ?? ""}
                          onChange={(date) => {
                            const dateOfBirth = toDateValue(date);
                            setEditForm((currentForm) => ({
                              ...currentForm,
                              date_of_birth: dateOfBirth,
                              age: calculateAge(dateOfBirth),
                            }));
                          }}
                          testId={`edit-field-${key}`}
                        />
                      ) : (
                        <Input
                          type={
                            key === "age"
                              ? "number"
                              : key.endsWith("email")
                                ? "email"
                                : "text"
                          }
                          value={editForm[key] ?? ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, [key]: e.target.value })
                          }
                          readOnly={key === "age"}
                          className="mt-1 bg-white focus-visible:ring-[var(--accent)]"
                          data-testid={`edit-field-${key}`}
                        />
                      )}
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                      UAE citizen?
                    </Label>
                    <Select
                      value={editForm.uae_citizen || "no"}
                      onValueChange={(value) =>
                        setEditForm({ ...editForm, uae_citizen: value })
                      }
                    >
                      <SelectTrigger className="mt-1 bg-white" data-testid="edit-field-uae-citizen">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </section>
              <section
                className="border-t border-[var(--subtle)] bg-[var(--bg)] px-5 py-5 sm:px-6 sm:py-6"
                data-testid="edit-application-documents"
              >
                <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-[var(--subtle)] pb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-[var(--accent)]" />
                    <div>
                      <h3 className="font-display text-base font-bold text-[var(--text-primary)]">
                        Application documents
                      </h3>
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        {attachedEditDocumentCount} file{attachedEditDocumentCount === 1 ? "" : "s"} across {documentTypesWithFiles} document{documentTypesWithFiles === 1 ? " type" : " types"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-5">
                  {editDocumentGroups.map(({ role, documents }) => {
                    const documentOwnerLabel = documentOwnerLabelForRole(role);
                    const attachedFiles = documents.reduce(
                      (count, document) =>
                        count +
                        document.file_ids.length +
                        (editDocumentFiles[document.key] || []).length,
                      0,
                    );
                    return (
                      <section
                        key={role}
                        className="overflow-hidden rounded-md border border-[var(--subtle)] bg-white"
                        data-testid={`edit-document-group-${role}`}
                      >
                        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--subtle)] bg-white px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[var(--accent)]" />
                            <div>
                              <h4 className="font-display text-sm font-bold text-[var(--text-primary)]">
                                {documentOwnerLabel} documents
                              </h4>
                              <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                                {attachedFiles} file{attachedFiles === 1 ? "" : "s"} attached
                              </p>
                            </div>
                          </div>
                        </header>
                        <div className="overflow-x-auto">
                          <table className="grid-table min-w-[860px] w-full text-sm">
                            <thead>
                              <tr>
                                <th className="px-3 py-2.5 text-left">Kind</th>
                                <th className="w-44 px-3 py-2.5 text-left">Number</th>
                                <th className="w-40 px-3 py-2.5 text-left">Expiry</th>
                                <th className="min-w-72 px-3 py-2.5 text-left">Files</th>
                                <th className="w-28 px-3 py-2.5 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {documents.map((document) => {
                                const documentKind = admissionDocumentKindByValue[document.kind];
                                const documentLabel =
                                  document.legacy_label ||
                                  documentKind?.label ||
                                  humanizeStatus(document.kind);
                                const pendingFiles = editDocumentFiles[document.key] || [];
                                const uploadId = `edit-document-${document.key.replace(/[^a-z0-9_-]/gi, "-")}`;
                                const isUploadOnly = Boolean(documentKind?.metadata?.upload_only);
                                const fileCount = document.file_ids.length + pendingFiles.length;
                                const needsFile = !fileCount && document.requirement === "required";
                                const isOptional = document.requirement === "optional";
                                return (
                                  <tr
                                    key={document.key}
                                    data-testid={`edit-document-row-${document.key}`}
                                  >
                                    <td className="px-3 py-3 align-top">
                                      <div className="font-medium text-[var(--text-primary)]">
                                        {documentLabel}
                                      </div>
                                      <Badge
                                        className={`mt-1 border-0 text-[10px] ${fileCount ? "bg-emerald-100 text-emerald-700" : needsFile ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`}
                                      >
                                        {fileCount
                                          ? "Attached"
                                          : needsFile
                                            ? "Needs file"
                                            : isOptional
                                              ? "Optional"
                                              : "No file"}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-3 align-top">
                                      {isUploadOnly ? (
                                        <span className="text-xs text-[var(--text-muted)]">—</span>
                                      ) : (
                                        <Input
                                          value={document.number}
                                          onChange={(event) =>
                                            updateEditDocument(document.key, {
                                              number: event.target.value,
                                            })
                                          }
                                          className="h-8 bg-white text-xs"
                                          disabled={editMut.isPending}
                                          data-testid={`edit-document-number-${document.key}`}
                                        />
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-top">
                                      {isUploadOnly ? (
                                        <span className="text-xs text-[var(--text-muted)]">—</span>
                                      ) : (
                                        <Input
                                          type="date"
                                          value={document.expiry_date}
                                          onChange={(event) =>
                                            updateEditDocument(document.key, {
                                              expiry_date: event.target.value,
                                            })
                                          }
                                          className="h-8 bg-white text-xs"
                                          disabled={editMut.isPending}
                                          data-testid={`edit-document-expiry-${document.key}`}
                                        />
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-top">
                                      <div className="space-y-1.5">
                                        {document.file_ids.map((fileId, index) => {
                                          const fileName =
                                            document.file_names[index] ||
                                            `Attached file ${index + 1}`;
                                          const FileIcon = isImageDocument(fileName)
                                            ? FileImage
                                            : FileText;
                                          return (
                                            <div
                                              key={fileId}
                                              className="flex min-w-0 items-center gap-1.5 rounded-md border border-[var(--subtle)] bg-[var(--bg)] px-2 py-1"
                                            >
                                              <FileIcon className="h-3.5 w-3.5 shrink-0 text-[var(--accent)]" />
                                              <span className="min-w-0 flex-1 truncate text-xs font-medium text-[var(--text-secondary)]">
                                                {fileName}
                                              </span>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 shrink-0"
                                                onClick={() =>
                                                  openStoredDocumentPreview(fileId, fileName)
                                                }
                                                aria-label={`Preview ${fileName}`}
                                                title={`Preview ${fileName}`}
                                                disabled={editMut.isPending}
                                                data-testid={`edit-document-preview-${fileId}`}
                                              >
                                                <Eye className="h-3.5 w-3.5" />
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                onClick={() =>
                                                  removeStoredEditDocumentFile(
                                                    document.key,
                                                    index,
                                                  )
                                                }
                                                aria-label={`Remove ${fileName}`}
                                                title={`Remove ${fileName}`}
                                                disabled={editMut.isPending}
                                                data-testid={`edit-document-remove-${document.key}-${index}`}
                                              >
                                                <X className="h-3.5 w-3.5" />
                                              </Button>
                                            </div>
                                          );
                                        })}
                                        {pendingFiles.map((file, index) => {
                                          const FileIcon = isImageDocument(file.name, file.type)
                                            ? FileImage
                                            : FileText;
                                          return (
                                            <div
                                              key={`${file.name}-${index}`}
                                              className="flex min-w-0 items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-2 py-1"
                                            >
                                              <FileIcon className="h-3.5 w-3.5 shrink-0 text-sky-700" />
                                              <span className="min-w-0 flex-1 truncate text-xs font-medium text-sky-800">
                                                {file.name}
                                              </span>
                                              <Badge className="border-0 bg-sky-100 text-[10px] text-sky-700">
                                                Ready
                                              </Badge>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 shrink-0 text-sky-700 hover:bg-sky-100 hover:text-sky-900"
                                                onClick={() => openPendingDocumentPreview(file)}
                                                aria-label={`Preview ${file.name}`}
                                                title={`Preview ${file.name}`}
                                                disabled={editMut.isPending}
                                                data-testid={`edit-pending-document-preview-${document.key}-${index}`}
                                              >
                                                <Eye className="h-3.5 w-3.5" />
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                onClick={() =>
                                                  removePendingEditDocumentFile(
                                                    document.key,
                                                    index,
                                                  )
                                                }
                                                aria-label={`Discard ${file.name}`}
                                                title={`Discard ${file.name}`}
                                                disabled={editMut.isPending}
                                                data-testid={`edit-pending-document-remove-${document.key}-${index}`}
                                              >
                                                <X className="h-3.5 w-3.5" />
                                              </Button>
                                            </div>
                                          );
                                        })}
                                        {!fileCount ? (
                                          <span className="text-xs text-[var(--text-muted)]">
                                            {needsFile
                                              ? "No file attached"
                                              : isOptional
                                                ? "Optional document"
                                                : "No file attached"}
                                          </span>
                                        ) : null}
                                      </div>
                                    </td>
                                    <td className="px-3 py-3 text-right align-top">
                                      <Input
                                        id={uploadId}
                                        type="file"
                                        multiple={document.source === "wizard"}
                                        accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                                        className="!sr-only"
                                        onChange={(event) =>
                                          selectEditDocument(document, event)
                                        }
                                        disabled={editMut.isPending}
                                        data-testid={`edit-document-${document.key}`}
                                      />
                                      <Label
                                        htmlFor={uploadId}
                                        className={`inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-[var(--accent)] bg-white px-2.5 text-xs font-semibold text-[var(--accent)] transition-colors hover:bg-orange-50 ${editMut.isPending ? "pointer-events-none opacity-50" : ""}`}
                                        data-testid={`edit-document-upload-${document.key}`}
                                      >
                                        <Upload className="h-3.5 w-3.5" />
                                        {fileCount ? "Add file" : "Upload"}
                                      </Label>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    );
                  })}
                </div>
              </section>
            </div>
            <DialogFooter className="border-t border-[var(--subtle)] bg-white px-5 py-4 sm:px-6">
              <Button
                type="button"
                variant="outline"
                onClick={closeEdit}
                disabled={editMut.isPending}
                data-testid="cancel-application-changes"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => editMut.mutate()}
                disabled={editMut.isPending || !hasEditChanges}
                className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
                data-testid="save-application-changes"
              >
                {editMut.isPending ? "Saving changes..." : "Save changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={Boolean(documentPreview)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setDocumentPreview(null);
          }}
        >
          <DialogContent className="max-w-5xl overflow-hidden p-0">
            <DialogHeader className="border-b border-[var(--subtle)] bg-white px-5 py-4 pr-12 sm:px-6">
              <DialogTitle className="font-display text-lg text-[var(--text-primary)]">
                Document preview
              </DialogTitle>
              <DialogDescription className="truncate">
                {documentPreview?.fileName || "Selected document"}
              </DialogDescription>
            </DialogHeader>
            <div className="flex min-h-[45vh] items-center justify-center bg-[var(--bg)] p-4 sm:p-6">
              {documentPreview?.loading ? (
                <div className="flex flex-col items-center gap-3 text-sm text-[var(--text-muted)]">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--accent)]" />
                  Loading document
                </div>
              ) : documentPreview?.issue ? (
                <div
                  className="max-w-xl rounded-md border border-amber-200 bg-amber-50 p-5 text-center"
                  data-testid="document-preview-unavailable"
                >
                  <FileText className="mx-auto h-8 w-8 text-amber-700" />
                  <p className="mt-3 font-display text-base font-bold text-amber-950">
                    Preview unavailable
                  </p>
                  <p className="mt-1 text-sm leading-6 text-amber-900">
                    {documentPreview.issue}
                  </p>
                </div>
              ) : isImageDocument(
                  documentPreview?.fileName,
                  documentPreview?.contentType,
                ) ? (
                <img
                  src={documentPreview?.url}
                  alt={documentPreview?.fileName || "Document preview"}
                  className="max-h-[65vh] max-w-full rounded-md border border-[var(--subtle)] bg-white object-contain"
                />
              ) : (
                <iframe
                  title={documentPreview?.fileName || "Document preview"}
                  src={documentPreview?.url}
                  className="h-[65vh] w-full rounded-md border border-[var(--subtle)] bg-white"
                />
              )}
            </div>
            {!documentPreview?.loading && documentPreview?.url ? (
              <DialogFooter className="border-t border-[var(--subtle)] bg-white px-5 py-3 sm:px-6">
                <Button asChild type="button" variant="outline">
                  <a
                    href={documentPreview.url}
                    download={documentPreview.fileName || "document"}
                  >
                    <Download className="h-4 w-4" /> Download
                  </a>
                </Button>
              </DialogFooter>
            ) : null}
          </DialogContent>
        </Dialog>
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
          <Card className="border-[var(--subtle)]">
            <CardContent className="flex items-center justify-between gap-3 p-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                  {statusLabel("enrolled")}
                </div>
                <div className="font-display text-2xl font-bold text-[var(--success)]">
                  {(groupedByStatus.enrolled || []).length}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setDetailsStatus("enrolled");
                  setSelectedDetailsApplication(null);
                  setDetailsOpen(true);
                }}
                data-testid="view-enrolled-details"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" /> View details
              </Button>
            </CardContent>
          </Card>
          <Card className="border-[var(--subtle)]">
            <CardContent className="flex items-center justify-between gap-3 p-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                  {statusLabel("rejected")}
                </div>
                <div className="font-display text-2xl font-bold text-[var(--error)]">
                  {(groupedByStatus.rejected || []).length}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setDetailsStatus("rejected");
                  setSelectedDetailsApplication(null);
                  setDetailsOpen(true);
                }}
                data-testid="view-rejected-details"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" /> View details
              </Button>
            </CardContent>
          </Card>
        </div>
        <Card className="border-[var(--subtle)]" data-testid="admission-drafts-area">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[var(--text-primary)]">{t("admissionWizard.savedDraft")}</div>
                  <div className="text-xs text-[var(--text-muted)]">{t("admissionWizard.draftCount", { count: drafts.length })}</div>
                </div>
              </div>
              {draftsLoading ? <Skeleton className="h-16" /> : drafts.length ? (
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {drafts.map((draft) => (
                    <div
                      key={draft.id}
                      className="flex items-center gap-2 rounded-md border border-[var(--subtle)] bg-[var(--bg)] p-1.5 transition-colors hover:border-[var(--accent)] hover:bg-orange-50/40"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/admissions/drafts/${draft.id}`)}
                        className="min-w-0 flex-1 rounded-sm px-1.5 py-1.5 text-start focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                        data-testid={`resume-admission-draft-${draft.id}`}
                      >
                        <div className="truncate text-sm font-semibold">{draft.people?.child?.first_name || t("admissionWizard.untitled")} {draft.people?.child?.last_name || ""}</div>
                        <div className="mt-1 truncate text-xs text-[var(--text-muted)]">{draft.people?.father?.email || draft.people?.mother?.email || t("admissionWizard.familyPending")}</div>
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => requestDelete(draft, "draft")}
                        aria-label={`Delete draft for ${draft.people?.child?.first_name || t("admissionWizard.untitled")} ${draft.people?.child?.last_name || ""}`}
                        title="Delete draft"
                        data-testid={`delete-draft-${draft.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : <div className="rounded-md border border-dashed border-[var(--subtle)] py-6 text-center text-sm text-[var(--text-muted)]">{t("admissionWizard.noDrafts")}</div>}
            </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pipelineStatuses.map((stage) => {
            const items = groupedByStatus[stage.key] || [];
            return (
              <div
                key={stage.key}
                className="rounded-md bg-white border border-[var(--subtle)]"
              >
                <div className="p-3 border-b border-[var(--subtle)] flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                    {stage.label}
                  </div>
                  <Badge className={`${stage.tone} border-0`}>
                    {items.length}
                  </Badge>
                </div>
                <div className="p-2 space-y-2 min-h-[60px]">
                  {items.map((app) => (
                    <div
                      key={app.id}
                      className="group relative overflow-hidden rounded-lg border border-[var(--subtle)] bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--accent)] hover:shadow-md"
                      data-testid={`app-${app.application_no}`}
                    >
                      <div className="flex items-start justify-between gap-2 border-b border-[var(--subtle)] bg-[var(--bg)] px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                            {app.application_no}
                          </div>
                          <div className="truncate pr-1 font-display text-base font-bold text-[var(--text-primary)]">
                            {app.applicant_first_name} {app.applicant_last_name}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(app)}
                            aria-label={`Edit ${app.applicant_first_name} ${app.applicant_last_name}`}
                            title="Edit application"
                            data-testid={`edit-${app.id}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--subtle)] bg-white text-[var(--text-muted)] transition-colors hover:border-[var(--accent)] hover:bg-orange-50 hover:text-[var(--accent)]"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {canDeleteApplication(app) ? (
                            <button
                              type="button"
                              onClick={() => requestDelete(app, "application")}
                              aria-label={`Delete ${app.applicant_first_name} ${app.applicant_last_name}`}
                              title="Delete application"
                              data-testid={`delete-application-${app.id}`}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-white text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-2 px-3 py-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                            Current stage
                          </span>
                          <Badge
                            className={`${stage.tone} border-0 text-[10px]`}
                          >
                            {statusLabel(app.status)}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
                              Guardian phone
                            </div>
                            <div className="truncate font-medium text-[var(--text-secondary)]">
                              {app.guardian_phone ||
                                app.father_guardian_phone ||
                                "Not provided"}
                            </div>
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
                              Date applied
                            </div>
                            <div className="truncate font-medium text-[var(--text-secondary)]">
                              {app.created_at
                                ? formatDate(app.created_at)
                                : "Not provided"}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 border-t border-[var(--subtle)] bg-[var(--bg)] px-3 py-2.5">
                        {(transitionMap[app.status] || []).map((to) =>
                          (() => {
                            const transition = TRANSITION_BUTTON_META[to] || {};
                            const TransitionIcon = TRANSITION_ICONS[to] || ArrowRight;
                            const className =
                              admissionStatusByKey[to]?.metadata
                                ?.transition_class ||
                              transition.className ||
                              "border-[var(--subtle)] bg-white text-[var(--text-secondary)] hover:border-[var(--accent)] hover:bg-orange-50 hover:text-[var(--accent)]";
                            return (
                              <button
                                key={to}
                                onClick={() => transitionMut.mutate({ id: app.id, to })}
                                data-testid={`transition-${app.id}-${to}`}
                                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${className}`}
                              >
                                <TransitionIcon className="h-3.5 w-3.5" />
                                {statusLabel(to)}
                              </button>
                            );
                          })(),
                        )}
                        {convertibleStatuses.has(app.status) ? (
                          <React.Fragment>
                            <button
                              onClick={() => {
                                const candidates = (sections || []).filter((section) => section.grade_id === app.grade_applied_id && section.academic_year_id === app.academic_year_id);
                                if (candidates.length > 1) {
                                  setSectionPickerApplication(app);
                                  setSectionPickerId("");
                                  return;
                                }
                                if (candidates.length === 1) convertMut.mutate({ id: app.id, section_id: candidates[0].id });
                                else convertMut.mutate({ id: app.id });
                              }}
                              data-testid={`convert-${app.id}`}
                              className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded bg-[var(--success)] text-white hover:bg-emerald-700"
                            >
                              {t("admissions.enroll")}
                            </button>
                            <span className="self-center text-xs text-[var(--text-muted)]">
                              {t("admissions.enrollSetsStatus")}
                            </span>
                          </React.Fragment>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={!!sectionPickerApplication} onOpenChange={(open) => !open && setSectionPickerApplication(null)}>
        <DialogContent aria-describedby="section-picker-description">
          <DialogHeader>
            <DialogTitle>{t("admissions.selectSectionTitle")}</DialogTitle>
            <DialogDescription id="section-picker-description">{t("admissions.selectSectionDescription")}</DialogDescription>
          </DialogHeader>
          <Select value={sectionPickerId} onValueChange={setSectionPickerId}>
            <SelectTrigger aria-label={t("admissions.section")} data-testid="conversion-section-select"><SelectValue placeholder={t("admissions.chooseSection")} /></SelectTrigger>
            <SelectContent>
              {(sections || []).filter((section) => section.grade_id === sectionPickerApplication?.grade_applied_id && section.academic_year_id === sectionPickerApplication?.academic_year_id).map((section) => (
                <SelectItem key={section.id} value={section.id}>{section.name}{section.capacity ? ` · ${section.remaining_capacity ?? section.capacity} places left` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionPickerApplication(null)}>{t("common.cancel")}</Button>
            <Button disabled={!sectionPickerId || convertMut.isPending} onClick={() => { convertMut.mutate({ id: sectionPickerApplication.id, section_id: sectionPickerId }); setSectionPickerApplication(null); }} data-testid="conversion-section-confirm">{convertMut.isPending ? t("common.saving") : t("admissions.enroll")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
